import { BidStatus } from "api";
import Loader from "components/shared/loader";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { applyOrganizationProcessingSettings, getOrganizationProcessingSettings } from "sources/shippingOrders";
import { applySettings, getSettings } from "sources/waybills";
import { ShippingOrderSettingsForm, WaybillMatchingSettingsForm } from "./form";
import {
    isFormValid,
    MatchingSettingsForm,
    selectableStatuses,
    toFormModel,
    toRequestModel,
    UpdateForm,
} from "./model";

type SettingsProps = {
    editable: boolean;
};

const SettingsLoader = () => (
    <div className="card card-body mb-3 position-relative" style={{ minHeight: 160 }}>
        <Loader wrapCname="content-center d-flex justify-content-center h-100" />
    </div>
);

const SettingsLoadError = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="card card-body mb-3">
        <div className="alert alert-danger mb-0" role="alert">
            <div className="mb-2">{message}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRetry}>
                Повторить
            </button>
        </div>
    </div>
);

const ShippingOrderProcessingSettings = ({ editable }: SettingsProps) => {
    const [disableMatching, setDisableMatching] = useState<boolean | null>(null);
    const [initialDisableMatching, setInitialDisableMatching] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [saveError, setSaveError] = useState(false);

    const loadSettings = useCallback(() => {
        setIsLoading(true);
        setLoadError(false);
        return getOrganizationProcessingSettings()
            .then((settings) => {
                if (settings === false) {
                    setLoadError(true);
                    return;
                }
                setDisableMatching(settings.disableMatching);
                setInitialDisableMatching(settings.disableMatching);
            })
            .catch(() => setLoadError(true))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const updateDisableMatching = (checked: boolean) => {
        setDisableMatching(checked);
        setSaveError(false);
    };

    const isDirty = disableMatching !== null && disableMatching !== initialDisableMatching;

    const saveSettings = () => {
        if (disableMatching === null || !isDirty || isSaving) {
            return;
        }
        setIsSaving(true);
        setSaveError(false);
        applyOrganizationProcessingSettings({ disableMatching })
            .then((saved) => {
                if (!saved) {
                    setSaveError(true);
                    return;
                }
                setInitialDisableMatching(disableMatching);
            })
            .catch(() => setSaveError(true))
            .finally(() => setIsSaving(false));
    };

    if (isLoading) {
        return <SettingsLoader />;
    }

    if (loadError || disableMatching === null) {
        return <SettingsLoadError message="Не удалось загрузить настройки обработки ЭЗЗ." onRetry={loadSettings} />;
    }

    return (
        <ShippingOrderSettingsForm
            disableMatching={disableMatching}
            editable={editable}
            onChange={updateDisableMatching}
            isSaving={isSaving}
            isDirty={isDirty}
            isValid
            saveError={saveError}
            onSave={saveSettings}
        />
    );
};

const WaybillMatchingSettings = ({ editable }: SettingsProps) => {
    const [form, setForm] = useState<MatchingSettingsForm | null>(null);
    const [initialForm, setInitialForm] = useState<MatchingSettingsForm | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [saveError, setSaveError] = useState(false);

    const loadSettings = useCallback(() => {
        setIsLoading(true);
        setLoadError(false);
        return getSettings()
            .then((settings) => {
                if (settings === false) {
                    setLoadError(true);
                    return;
                }

                const formModel = toFormModel(settings);
                setForm(formModel);
                setInitialForm(formModel);
            })
            .catch(() => setLoadError(true))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const updateForm = useCallback<UpdateForm>((updater) => {
        setForm((current) => (current ? updater(current) : current));
        setSaveError(false);
    }, []);

    const toggleStatus = useCallback(
        (status: BidStatus, checked: boolean) => {
            if (status === BidStatus.Started) {
                return;
            }
            updateForm((current) => {
                const statuses = new Set(current.bid.allowedStatuses);
                if (checked) {
                    statuses.add(status);
                } else {
                    statuses.delete(status);
                }
                return {
                    ...current,
                    bid: {
                        ...current.bid,
                        allowedStatuses: selectableStatuses
                            .filter(({ value }) => statuses.has(value))
                            .map(({ value }) => value),
                    },
                };
            });
        },
        [updateForm],
    );

    const isDirty = useMemo(
        () => !!form && !!initialForm && JSON.stringify(form) !== JSON.stringify(initialForm),
        [form, initialForm],
    );
    const valid = form ? isFormValid(form) : false;

    const saveSettings = () => {
        if (!form || !valid || !isDirty || isSaving) {
            return;
        }
        setIsSaving(true);
        setSaveError(false);
        applySettings(toRequestModel(form))
            .then((saved) => {
                if (!saved) {
                    setSaveError(true);
                    return;
                }
                setInitialForm(form);
            })
            .catch(() => setSaveError(true))
            .finally(() => setIsSaving(false));
    };

    if (isLoading) {
        return <SettingsLoader />;
    }

    if (loadError || !form) {
        return (
            <SettingsLoadError message="Не удалось загрузить настройки сопоставления ЭТрН." onRetry={loadSettings} />
        );
    }

    return (
        <WaybillMatchingSettingsForm
            form={form}
            editable={editable}
            updateForm={updateForm}
            isSaving={isSaving}
            isDirty={isDirty}
            isValid={valid}
            saveError={saveError}
            onSave={saveSettings}
            onToggleStatus={toggleStatus}
        />
    );
};

const EpdSettings = ({ editable }: SettingsProps) => (
    <div className="profile__tab-wrapper">
        <ShippingOrderProcessingSettings editable={editable} />
        <WaybillMatchingSettings editable={editable} />
    </div>
);

export default EpdSettings;
